import React, { useMemo } from 'react';
import { useBalloonEventsMetadata } from 'react-ui';
import { Language, CategoryModel } from 'ts-core';

interface CategorySelectorProps {
  selected: string[];
  onChange: (categories: string[]) => void;
  multiple?: boolean;
}

// Helper to convert hex to rgba
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface CategoryButtonProps {
  category: CategoryModel;
  isSelected: boolean;
  onClick: () => void;
}

const CategoryButton: React.FC<CategoryButtonProps> = ({ category, isSelected, onClick }) => {
  const displayName = category.displayName.getText(Language.en);
  
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:scale-105"
      style={{
        borderColor: category.color,
        backgroundColor: isSelected ? category.color : hexToRgba(category.color, 0.1),
        color: isSelected ? '#FFFFFF' : category.color,
      }}
      aria-pressed={isSelected}
      aria-label={`${displayName} category`}
    >
      <div 
        className="text-2xl mb-1"
        dangerouslySetInnerHTML={{__html: category.icon}}
        style={{
          fontFamily: 'fontAwesome',
        }}
      />
      <div className="text-xs font-medium">{displayName}</div>
    </button>
  );
};

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  selected,
  onChange,
  multiple = false,
}) => {
  const { metadata, loading } = useBalloonEventsMetadata();
  
  // Get all categories from metadata
  const allCategories = metadata?.getAllCategories() || [];
  
  // Filter to only top-level categories (no parent) and exclude system categories
  const topLevelCategories = useMemo(() => {
    return allCategories.filter((c: CategoryModel) => !c.parent && !c.isSystem);
  }, [allCategories]);
  
  // Get subcategories to display based on selected parent categories
  const subcategoriesToDisplay = useMemo(() => {
    const subcategories: CategoryModel[] = [];
    
    for (const category of topLevelCategories) {
      // Check if this parent category is selected
      if (selected.includes(category.slug) && category.children && category.children.length > 0) {
        subcategories.push(...category.children);
      }
    }
    
    return subcategories;
  }, [topLevelCategories, selected]);

  const handleCategoryClick = (categorySlug: string) => {
    if (multiple) {
      // Toggle selection for multiple mode
      if (selected.includes(categorySlug)) {
        onChange(selected.filter((slug) => slug !== categorySlug));
      } else {
        onChange([...selected, categorySlug]);
      }
    } else {
      // Single selection mode
      onChange([categorySlug]);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-gray-200 bg-gray-100 animate-pulse"
          >
            <div className="w-8 h-8 bg-gray-300 rounded mb-2"></div>
            <div className="w-16 h-3 bg-gray-300 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Show message if no categories
  if (topLevelCategories.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
        <p>No categories available</p>
        <p className="text-xs mt-1">Please check your metadata configuration</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Categories */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {topLevelCategories.map((category: CategoryModel) => (
          <CategoryButton
            key={category.slug}
            category={category}
            isSelected={selected.includes(category.slug)}
            onClick={() => handleCategoryClick(category.slug)}
          />
        ))}
      </div>
      
      {/* Subcategories Section */}
      {subcategoriesToDisplay.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-3">
            Subcategories <span className="text-gray-400">(optional - refine your selection)</span>
          </p>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {subcategoriesToDisplay.map((subcategory) => (
              <CategoryButton
                key={subcategory.slug}
                category={subcategory}
                isSelected={selected.includes(subcategory.slug)}
                onClick={() => handleCategoryClick(subcategory.slug)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
